// Single source of truth for "known" public routes. Used by:
//   - 404 page to suggest the closest match for typo'd URLs
//   - Cmd+K site search
//   - Future: SEO crawl assertions, breadcrumb labels
//
// Keep in sync with src/App.js when adding routes. The build-time sitemap
// generator also reads scripts/routes.config.json — that's the deploy-side
// source of truth; this file is the runtime UX layer.

export const ROUTES = [
  { path: '/',                            title: 'Home',                          tags: ['home', 'overview', 'technum'] },
  { path: '/about-us',                    title: 'About Us',                      tags: ['team', 'company', 'history'] },
  { path: '/blog',                        title: 'Blog',                          tags: ['articles', 'insights', 'news'] },
  { path: '/careers',                     title: 'Careers',                       tags: ['jobs', 'hiring', 'positions', 'work'] },
  { path: '/case-studies',                title: 'Case Studies',                  tags: ['customers', 'success', 'stories', 'portfolio'] },
  { path: '/case-studies/trident-sap-aws-migration', title: 'Trident SAP on AWS — Case Study', tags: ['sap', 'aws', 'migration', 'trident'] },
  { path: '/cloud-migration',             title: 'Cloud Consultancy & Migration', tags: ['aws', 'azure', 'gcp', 'cloud', 'migrate'] },
  { path: '/computer-vision',             title: 'Computer Vision Intelligence',  tags: ['cv', 'ai', 'vision', 'quality', 'inline'] },
  { path: '/contact',                     title: 'Contact Us',                    tags: ['talk', 'demo', 'meeting', 'email'] },
  { path: '/digital-transformation',      title: 'Digital Transformation',        tags: ['dx', 'transform', 'modernisation'] },
  { path: '/industries',                  title: 'Industries',                    tags: ['sectors', 'verticals'] },
  { path: '/industry-4-0',                title: 'Industry 4.0 Implementation',   tags: ['iot', 'i4', 'smart factory', 'iiot'] },
  { path: '/onified-ai',                  title: 'Onified.ai',                    tags: ['platform', 'product', 'eri', 'manufacturing'] },
  { path: '/privacy-policy',              title: 'Privacy Policy',                tags: ['legal', 'dpdp', 'gdpr', 'privacy'] },
  { path: '/terms-of-service',            title: 'Terms of Service',              tags: ['legal', 'terms'] },
  { path: '/technology-infrastructure',   title: 'Technology Infrastructure',     tags: ['network', 'datacenter', 'edge', 'hardware'] },
  { path: '/aws-partner',                 title: 'AWS Cloud Partner',             tags: ['aws', 'amazon', 'partner', 'apn'] },
  { path: '/automation',                  title: 'Automation Solutions',          tags: ['rpa', 'workflow', 'iot', 'automation'] },
  { path: '/materials-finishing',         title: 'Materials & Finishing',         tags: ['process', 'quality', 'finishing'] },
  { path: '/textiles-apparel',            title: 'Textiles & Apparel',            tags: ['textile', 'garment', 'apparel', 'fabric'] },
  { path: '/process-industries',          title: 'Process Industries',            tags: ['chemical', 'continuous', 'process'] },
  { path: '/precision-manufacturing',     title: 'Precision Manufacturing',       tags: ['cnc', 'precision', 'machined'] },
];

// ---- Fuzzy-match against a failed path -------------------------------------
//
// Damerau-Levenshtein for short strings is overkill; for marketing URLs we
// just need: tokenise both sides on non-alphanumerics, score by overlap
// (Jaccard-ish), break ties by Levenshtein distance on the joined string.

const tokenise = (s) => String(s).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  // Allocate one row + previous, walk in-place.
  let prev = new Array(n + 1);
  for (let j = 0; j <= n; j += 1) prev[j] = j;
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
};

const scoreCandidate = (failedTokens, candidate) => {
  const candidateTokens = new Set([
    ...tokenise(candidate.path),
    ...tokenise(candidate.title),
    ...(candidate.tags || []).flatMap(tokenise),
  ]);
  const overlap = failedTokens.filter((t) => candidateTokens.has(t)).length;
  const overlapScore = overlap / Math.max(failedTokens.length, 1);
  const distance = levenshtein(failedTokens.join(''), tokenise(candidate.path).join(''));
  // Higher overlap + smaller distance wins.
  return overlapScore * 100 - distance;
};

export const suggestRoutes = (failedPath, limit = 3) => {
  const failedTokens = tokenise(failedPath);
  if (!failedTokens.length) return [];
  return ROUTES
    .map((r) => ({ ...r, score: scoreCandidate(failedTokens, r) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};

// Full-text search over the registry. Used by the Cmd+K modal.
export const searchRoutes = (query, limit = 8) => {
  if (!query) return [];
  const needle = String(query).toLowerCase().trim();
  if (!needle) return [];
  const tokens = tokenise(needle);
  if (!tokens.length) return [];
  return ROUTES
    .map((r) => {
      const haystack = [r.path, r.title, ...(r.tags || [])].join(' ').toLowerCase();
      const allTokensMatch = tokens.every((t) => haystack.includes(t));
      if (!allTokensMatch) return null;
      // Substring score: prefer when the query is a prefix of the title.
      let score = 0;
      if (r.title.toLowerCase().startsWith(needle)) score += 100;
      if (r.path.includes(needle)) score += 50;
      tokens.forEach((t) => { if (r.title.toLowerCase().includes(t)) score += 10; });
      return { ...r, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};
